import { Injectable } from '@angular/core';
import {
  AllDatasourcesQuery,
  DatasourceColumnUIModel,
  DatasourceTableUIModel,
  DatasourceUIModel,
} from '@y42/datasource';
import { AbstractFeatureQueryEntity } from '@y42/feature';
import { CanvasElementType, ColumnTypeEnum, Y42FeatureType } from '@y42/json-schema';
import { distinctUntilArrayChanged } from '@y42/rxjs';
import { areArraysEqual } from '@y42/util/are-arrays-equal';
import { combineLatest, map, Observable, of, shareReplay } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CanvasFilterStoreModel } from '../canvas-element/models/canvas-filter.model';
import { VisualizationCanvasElementStoreQuery } from '../canvas-element/visualization-canvas-element.store-query';
import { VisualizationTabStoreQuery } from '../tab/visualization-tab.store-query';
import { FilterState } from './filter-state.enum';
import { VisualizationFilterState, VisualizationFilterUIModel } from './visualization-filter.model';
import { VisualizationFilterStore } from './visualization-filter.store';

@Injectable({ providedIn: 'root' })
export class VisualizationFilterQuery extends AbstractFeatureQueryEntity<
  VisualizationFilterState,
  VisualizationFilterUIModel
> {
  constructor(
    protected readonly store: VisualizationFilterStore,
    protected readonly tabQuery: VisualizationTabStoreQuery,
    protected readonly allDatasourcesQuery: AllDatasourcesQuery,
    protected readonly canvasElementQuery: VisualizationCanvasElementStoreQuery,
  ) {
    super({
      store,
      featureType: Y42FeatureType.VisualizationFilter,
      toUIModel: (storeModel) => ({
        featureType: Y42FeatureType.VisualizationFilter,
        ...storeModel,
      }),
    });
  }

  readonly items$: Observable<VisualizationFilterUIModel[]> = this.selectAll().pipe(
    map((filters) => filters.map((filter) => this.toUIModel(filter))),
  );

  readonly active$: Observable<VisualizationFilterUIModel | undefined> = this.selectActive().pipe(
    map((filter) => (filter ? this.toUIModel(filter) : undefined)),
  );

  readonly itemsWithContext$: Observable<
    Array<{
      filter: VisualizationFilterUIModel;
      datasource: DatasourceUIModel | undefined;
      table: DatasourceTableUIModel | undefined;
      column: DatasourceColumnUIModel | undefined;
      state: FilterState;
    }>
  > = combineLatest([this.items$, this.allDatasourcesQuery.datasourceTableDictionary$]).pipe(
    map(([filters, tableDictionary]) =>
      filters.map((filter) => {
        const tableContext = filter.datasource_table_path
          ? tableDictionary[filter.datasource_table_path]
          : { datasource: undefined, table: undefined };
        const datasource = tableContext?.datasource;
        const table = tableContext?.table;

        if (table && !table.jobs.length) {
          return { filter, datasource, table, column: undefined, state: FilterState.Valid };
        }

        const column = table?.latest_valid_job?.columns?.find((col) => col.field_name === filter.field_name);

        let state = FilterState.Valid;
        if (!column) {
          state = FilterState.ColumnDeleted;
        } else if (column.data_type !== filter.data_type) {
          state = FilterState.ColumnTypeDiff;
        }

        return { filter, datasource, table, column, state };
      }),
    ),
    distinctUntilArrayChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly itemsWithContextForActiveTab$ = combineLatest([
    this.tabQuery.selectActive().pipe(
      switchMap((activeTab) => {
        if (!activeTab) {
          return of([]);
        }
        return combineLatest(activeTab.$children.map((path) => this.canvasElementQuery.selectEntity(path)));
      }),
      map(
        (childrenCanvasElements) =>
          childrenCanvasElements.filter((e) => e && e.type === CanvasElementType.Filter) as CanvasFilterStoreModel[],
      ),
      distinctUntilChanged((a, b) => {
        const compareByFn = (e: CanvasFilterStoreModel) => e.typeSettings?.filterPath;
        return areArraysEqual(a.map(compareByFn), b.map(compareByFn));
      }),
    ),
    this.itemsWithContext$,
  ]).pipe(
    map(([filterCanvasElementsForActiveTab, items]) => {
      if (!filterCanvasElementsForActiveTab) {
        return [];
      }
      return items.filter(({ filter }) => {
        const filterBelongsToActiveTab = !!filterCanvasElementsForActiveTab.find(
          (e) => e.typeSettings?.filterPath === filter.$path,
        );
        return filterBelongsToActiveTab;
      });
    }),
    distinctUntilArrayChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly activeDashboardFilters$ = this.itemsWithContextForActiveTab$.pipe(
    map((items) =>
      items
        .filter(({ state }) => state === FilterState.Valid) // exclude invalid filters
        .map(({ filter: globalFilter }) => ({
          variable_field_name: globalFilter.field_name || '',
          variable_type: globalFilter.data_type || ColumnTypeEnum.String,
          datasource_table_path: globalFilter.datasource_table_path,
          operations: globalFilter.operations || [],
        })),
    ),
  );

  getAllUIModels(): VisualizationFilterUIModel[] {
    return this.getAll().map((filter) => this.toUIModel(filter));
  }

  readonly toListViewModel = (item: VisualizationFilterUIModel) => [item];
}
